import React, { useRef, useEffect, useState, useCallback, useContext} from "react";
import { useNavigate } from "react-router-dom";

import ProgressBar from "../components/ProgressBar";
import NavPanel from "../components/NavPanel";
import TapArea from "../components/TapArea";
import { AudioContext } from "../AudioContext";
import Popup from "../components/Popup";

import styles from "./AthleteCloset.module.css";

const AthleteCloset = ({
  products = [],
  audioPath,
  previousPath,
  nextPath,
  backgroundImg,
  progressStep = 3,
  title,
  trackEventName = 'Client Link - Closet'
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef(null);
  const itemRefs = useRef([]);
  const navigate = useNavigate();
  const { muted, setMuted } = useContext(AudioContext);
  const videoRef = useRef(null);
  const [popupVisible, setPopupVisible] = useState(false);

  // Programmatic scroll guard
  const isProgrammaticRef = useRef(false);
  const programmaticTimerRef = useRef(null);

  const onPreviousPageClick = useCallback(() => {
    if (previousPath) navigate(previousPath);
  }, [navigate, previousPath]);

  const onNextPageClick = useCallback(() => {
    if (nextPath) navigate(nextPath);
  }, [navigate, nextPath]);

  const handleMuteToggle = () => {
    setMuted((prev) => {
      const newMuted = !prev;
      if (videoRef.current) {
        videoRef.current.muted = newMuted;
      }
      return newMuted;
    });
  };

  // Initial positioning - ensure first product is centered
  useEffect(() => {
    if (carouselRef.current && itemRefs.current[0]) {
      setTimeout(() => {
        itemRefs.current[0].scrollIntoView({
          behavior: "auto",
          inline: "center",
          block: "nearest"
        });
      }, 50);
    }
  }, []);

  // Scroll to the active product when activeIndex changes, only for programmatic triggers
  useEffect(() => {
    if (!isProgrammaticRef.current) return;

    if (carouselRef.current && itemRefs.current[activeIndex]) {
      itemRefs.current[activeIndex].scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest"
      });
    }
  }, [activeIndex]);

  const onClickLink = (link) => {
    try {
      umami?.track(trackEventName, { link: link });
    } catch {}
    if (link) setPopupVisible(true);
  };

  // Detect which product is centered (only when not programmatically scrolling)
  useEffect(() => {
    const handleScroll = () => {
      if (!carouselRef.current) return;
      if (isProgrammaticRef.current) return;
      const carousel = carouselRef.current;
      const carouselRect = carousel.getBoundingClientRect();
      let minDiff = Infinity;
      let newActive = 0;

      // Check all items including the Shop More item
      const totalItems = products.length ;
      for (let idx = 0; idx < totalItems; idx++) {
        const item = itemRefs.current[idx];
        if (!item) continue;
        const rect = item.getBoundingClientRect();
        const itemCenter = rect.left + rect.width / 2;
        const carouselCenter = carouselRect.left + carouselRect.width / 2;
        const diff = Math.abs(itemCenter - carouselCenter);

        if (diff < minDiff) {
          minDiff = diff;
          newActive = idx;
        }
      }
      setActiveIndex(newActive);
    };

    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener("scroll", handleScroll, { passive: true });
    }

    return () => {
      if (carousel) {
        carousel.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const onDotClick = (idx) => {
    isProgrammaticRef.current = true;
    if (programmaticTimerRef.current) clearTimeout(programmaticTimerRef.current);
    programmaticTimerRef.current = setTimeout(() => {
      isProgrammaticRef.current = false;
    }, 700);
    setActiveIndex(idx);
  };

  return (
    <div className={styles.closet}>
      {audioPath && <audio src={audioPath} ref={videoRef} muted={muted} autoPlay/>  }

      {previousPath && <NavPanel onClick={onPreviousPageClick} />}
      {nextPath && <NavPanel position='right' onClick={onNextPageClick} />}

      {previousPath && <TapArea style={styles.tapPrevious} onClick={onPreviousPageClick} />}
      {nextPath && <TapArea style={styles.tapNext} onClick={onNextPageClick} />}

      <ProgressBar current={String(progressStep)} />

      {backgroundImg && (
        <img
          src={backgroundImg}
          className={`${styles.backgroundImg}`}
          alt="Closet Background"
        />
      )}
      <div className={styles.gradient}></div>

      <div style={{ position: 'absolute', inset: 0, zIndex: 2, background: 'rgba(0, 0, 0, 0.20)' }} />

      <div className={styles.checkoutText} dangerouslySetInnerHTML={{ __html: title }} />

      <div className={styles.carouselWrapper}>
        <div className={styles.carouselContainer}>
          <div className={styles.carousel} ref={carouselRef}>
            {products.map((product, idx) => (
              <div
                key={product.id ?? idx}
                className={idx === activeIndex ? `${styles.productCard} ${styles.active}` : styles.productCard}
                ref={el => (itemRefs.current[idx] = el)}
                onClick={() => onClickLink(product.link)}
              >
                <img className={styles.productImg} src={product.image} />
                <div className={styles.productInfo}>
                  <div className={styles.productDesc}>{product.desc}</div>
                  <div className={styles.productName}>{product.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.dots}>
          {products.map((_, idx) => (
            <span
              key={idx}
              className={idx === activeIndex ? styles.dotActive : styles.dot}
              onClick={() => onDotClick(idx)}
            />
          ))}
        </div>
      </div>

      { audioPath &&
        <button
          className={styles.muteButton}
          onClick={handleMuteToggle}
          aria-label={muted ? "Unmute video" : "Mute video"}
        >
          {muted ? (
            // Mute icon (simple SVG)
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><line x1="23" y1="9" x2="17" y2="15"></line><line x1="17" y1="9" x2="23" y2="15"></line></svg>
          ) : (
            // Unmute icon (simple SVG)
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M19 12c0-1.77-.77-3.29-2-4.29M19 12c0 1.77-.77 3.29-2 4.29M15 8.71V15.3"></path></svg>
          )}
        </button>
      }
      <Popup visible={popupVisible} y={335} onHide={() => setPopupVisible(false)}>
        Redirects to product page on New Balance website.
      </Popup>
    </div>
      
  );
};

export default AthleteCloset;


